import { useCallback, useMemo } from 'react';
import { arrayOf, object, shape, string } from 'prop-types';
import _, { isEqual, isObject, keys, pick } from 'lodash';
import { useLocation } from 'react-router-dom';

import { useFilterState } from '../../../stores/FilterStateStore';
import { getUrlParamFromEncodedState } from '../../../utilities/FilterStateUrlUtilities';
import { addPlmPrefix, usePlmId } from '../../../utilities/PgmUtilities';

export const linkType = shape({
    target: string.isRequired,
    filters: arrayOf(string),
    params: object,
    encodedFilterState: object,
});

function buildSearch(encodedFilterState, filters, params) {
    const paramString =
        _.reduce(
            params,
            (p, value, key) => {
                p.set(key, value);
                return p;
            },
            new URLSearchParams()
        )?.toString() || '';

    if (!_.isEmpty(filters)) {
        return `?${getUrlParamFromEncodedState(_.pick(encodedFilterState, filters || []), paramString)}`;
    }

    return _.isEmpty(paramString) ? '' : `?${paramString}`;
}

function buildUrl(target, encodedFilterState, filters, params) {
    return `${target}${buildSearch(encodedFilterState, filters, params)}`;
}

export function useUrl(link) {
    const [filterState] = useFilterState();

    const url = useMemo(
        () =>
            buildUrl(
                link.target,
                { ...(filterState?.encoded || {}), ...(link.encodedFilterState || {}) },
                link.filters,
                link.params
            ),
        [link, filterState]
    );

    return url;
}

/**
 * Returns a callback function which can be used to determine if a given link is
 * the current active link or not. This function also accepts an options argument
 * to allow child links to be tested against the URL too.
 *
 * @param {Object} options
 * @returns {Function}
 */
export function useIsLinkActive(options) {
    const { includePlmId = true } = options || {};

    const [filterState] = useFilterState();
    const { pathname: currentPath } = useLocation();
    const plmId = usePlmId();

    return useCallback(
        (link, cbOptions) =>
            isActive({
                link,
                plmId: includePlmId ? plmId : null,
                currentPath,
                filterState,
                includeChildren: cbOptions?.includeChildren,
            }),
        [currentPath, filterState, includePlmId, plmId]
    );
}

/**
 * Tests whether or not a given link is active

 * @returns {Boolean}
 */
export function useIsActive(link, activeOptions, factoryOptions) {
    return useIsLinkActive(factoryOptions)(link, activeOptions);
}

function isActive(options) {
    const { link, plmId, currentPath: initialCurrentPath, filterState, includeChildren } = options;

    let target;
    let encodedFilterState;

    if (isObject(link)) {
        target = link.target;
        encodedFilterState = link.encodedFilterState;
    } else {
        target = link;
    }

    const targetPath = handleSlashes(addPlmPrefix(target, plmId));
    const currentPath = handleSlashes(initialCurrentPath);

    let pathEquals;

    if (includeChildren === true) {
        pathEquals = currentPath.startsWith(targetPath);
    } else {
        pathEquals = currentPath === targetPath;
    }

    if (pathEquals && encodedFilterState) {
        return filterStateIsEqual(encodedFilterState, filterState);
    }

    return pathEquals;
}

function filterStateIsEqual(linkEncodedFilterState, filterState) {
    if (!linkEncodedFilterState) return true;

    const filterIds = keys(linkEncodedFilterState);
    const pickedFilterState = pick(filterState?.encoded, filterIds);

    return isEqual(linkEncodedFilterState, pickedFilterState);
}

function handleSlashes(url) {
    if (!url || typeof url !== 'string') return url;

    if (url.endsWith('/')) {
        url = url.substr(0, url.length);
    }

    if (!url.startsWith('/')) {
        url = `/${url}`;
    }

    return url;
}
